import axios from 'axios';
import {
  ADMIN_SESSION_EXPIRED_EVENT,
  ADMIN_SESSION_EXPIRED_MESSAGE,
  clearAdminToken,
  getAdminToken,
  redirectToAdminLogin,
  setAdminSessionNotice,
} from './adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
// Increased timeout for satellite/high-latency connections
const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30 seconds timeout for slow connections
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const requireArrayResponse = (data, label) => {
  if (Array.isArray(data)) return data;
  throw new Error(`Unexpected ${label} API response shape`);
};

const requestHasAdminToken = (error) => {
  const headers = error?.config?.headers;
  return Boolean(headers?.Authorization || headers?.authorization);
};

export const hasPotentialUnsavedAdminWork = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (!window.location.pathname.startsWith('/admin/dashboard')) return false;

  if (document.querySelector('[data-admin-unsaved="true"], [role="dialog"]')) {
    return true;
  }

  const activeElement = document.activeElement;
  if (!activeElement?.matches?.('input, textarea, select, [contenteditable="true"]')) {
    return false;
  }

  if (activeElement.matches('input[type="password"]')) return false;
  return Boolean(activeElement.value?.trim?.() || activeElement.textContent?.trim?.());
};

export const handleAdminUnauthorized = (
  error,
  {
    clearToken = clearAdminToken,
    setNotice = setAdminSessionNotice,
    redirect = redirectToAdminLogin,
  } = {}
) => {
  if (error?.response?.status !== 401 || !requestHasAdminToken(error)) {
    return false;
  }

  clearToken();
  setNotice(ADMIN_SESSION_EXPIRED_MESSAGE);

  const event = new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT, {
    cancelable: true,
    detail: {
      message: ADMIN_SESSION_EXPIRED_MESSAGE,
      preserveWork: hasPotentialUnsavedAdminWork(),
    },
  });

  const shouldRedirect = window.dispatchEvent(event);
  if (shouldRedirect) redirect();
  return true;
};

let adminUnauthorizedHandled = false;

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!adminUnauthorizedHandled && handleAdminUnauthorized(error)) {
      adminUnauthorizedHandled = true;
    }

    // Handle timeout errors gracefully
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. Please check your internet connection and try again.';
    }
    return Promise.reject(error);
  }
);

// ==================== PUBLIC APIs ====================

// Get packages (public)
export const getPackages = async () => {
  try {
    const response = await api.get('/public/packages');
    return requireArrayResponse(response.data, 'packages');
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw error;
  }
};

// Get modules (public)
export const getModules = async (categoryId = null) => {
  try {
    const params = categoryId ? { category_id: categoryId } : {};
    const response = await api.get('/public/modules', { params });
    return requireArrayResponse(response.data, 'modules');
  } catch (error) {
    console.error('Error fetching modules:', error);
    throw error;
  }
};

// Get categories (public)
export const getCategories = async () => {
  try {
    const response = await api.get('/public/categories');
    return requireArrayResponse(response.data, 'categories');
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Get modules by category (public)
export const getModulesByCategory = async () => {
  try {
    const response = await api.get('/public/modules-by-category');
    return requireArrayResponse(response.data, 'modules by category');
  } catch (error) {
    console.error('Error fetching modules by category:', error);
    throw error;
  }
};

// Get plans (public)
export const getPlans = async () => {
  try {
    const response = await api.get('/public/plans');
    return requireArrayResponse(response.data, 'plans');
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
};

export const getPublicPricingRegions = async () => {
  try {
    const response = await api.get('/public/pricing-regions');
    return response.data;
  } catch (error) {
    console.error('Error fetching pricing regions:', error);
    throw error;
  }
};

export const getPublicRegionalPricing = async (region = null) => {
  try {
    const params = region ? { region } : {};
    const response = await api.get('/public/regional-pricing', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching regional pricing:', error);
    throw error;
  }
};

// ==================== LEAD/CONTACT APIs ====================

export const submitLead = async (leadData) => {
  try {
    const response = await api.post('/leads/', leadData);
    return response.data;
  } catch (error) {
    console.error('Error submitting lead:', error);
    throw error;
  }
};

// ==================== CLIENT DEMO ACCESS ====================

export const requestDemoAccess = async (accessKey) => {
  const response = await api.post('/demo-access', { access_key: accessKey });
  return response.data;
};

// ==================== ORDER APIs ====================

export const createOrder = async (orderData) => {
  try {
    const response = await api.post('/orders/', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const getOrder = async (orderId) => {
  try {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

// ==================== AUTH APIs ====================

export const adminLogin = async (username, password) => {
  try {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const getCurrentAdmin = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current admin:', error);
    throw error;
  }
};

export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  try {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

export const createAdmin = async (username, password, role = 'admin') => {
  try {
    const response = await api.post('/auth/create-admin', { username, password, role });
    return response.data;
  } catch (error) {
    console.error('Error creating admin:', error);
    throw error;
  }
};

export const listAdmins = async () => {
  try {
    const response = await api.get('/auth/admins');
    return response.data;
  } catch (error) {
    console.error('Error listing admins:', error);
    throw error;
  }
};

export const deleteAdmin = async (adminId) => {
  try {
    const response = await api.delete(`/auth/admins/${adminId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting admin:', error);
    throw error;
  }
};

// ==================== ADMIN APIs ====================

export const getAdminRegionalPricing = async () => {
  try {
    const response = await api.get('/admin/regional-pricing');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin regional pricing:', error);
    throw error;
  }
};

export const updatePricingRegion = async (regionId, regionData) => {
  try {
    const response = await api.put(`/admin/regional-pricing/regions/${regionId}`, regionData);
    return response.data;
  } catch (error) {
    console.error('Error updating pricing region:', error);
    throw error;
  }
};

export const updateRegionalPrice = async (priceId, priceData) => {
  try {
    const response = await api.put(`/admin/regional-pricing/prices/${priceId}`, priceData);
    return response.data;
  } catch (error) {
    console.error('Error updating regional price:', error);
    throw error;
  }
};

export const publishRegionalPricing = async () => {
  try {
    const response = await api.post('/admin/regional-pricing/publish');
    return response.data;
  } catch (error) {
    console.error('Error publishing regional pricing:', error);
    throw error;
  }
};

export const getClientDemos = async () => {
  try {
    const response = await api.get('/admin/client-demos');
    return response.data;
  } catch (error) {
    console.error('Error fetching client demos:', error);
    throw error;
  }
};

export const createClientDemo = async (demoData) => {
  try {
    const response = await api.post('/admin/client-demos', demoData);
    return response.data;
  } catch (error) {
    console.error('Error creating client demo:', error);
    throw error;
  }
};

export const updateClientDemo = async (demoId, demoData) => {
  try {
    const response = await api.put(`/admin/client-demos/${demoId}`, demoData);
    return response.data;
  } catch (error) {
    console.error('Error updating client demo:', error);
    throw error;
  }
};

export const rotateClientDemoKey = async (demoId) => {
  try {
    const response = await api.post(`/admin/client-demos/${demoId}/rotate-key`);
    return response.data;
  } catch (error) {
    console.error('Error rotating client demo key:', error);
    throw error;
  }
};

export const getManagedContent = async (params = {}) => {
  try {
    const response = await api.get('/admin/content', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching managed content:', error);
    throw error;
  }
};

export const createManagedContent = async (contentData) => {
  try {
    const response = await api.post('/admin/content', contentData);
    return response.data;
  } catch (error) {
    console.error('Error creating managed content:', error);
    throw error;
  }
};

export const updateManagedContent = async (contentId, contentData) => {
  try {
    const response = await api.put(`/admin/content/${contentId}`, contentData);
    return response.data;
  } catch (error) {
    console.error('Error updating managed content:', error);
    throw error;
  }
};

export const publishManagedContent = async (contentId) => {
  try {
    const response = await api.post(`/admin/content/${contentId}/publish`);
    return response.data;
  } catch (error) {
    console.error('Error publishing managed content:', error);
    throw error;
  }
};

export const archiveManagedContent = async (contentId) => {
  try {
    const response = await api.post(`/admin/content/${contentId}/archive`);
    return response.data;
  } catch (error) {
    console.error('Error archiving managed content:', error);
    throw error;
  }
};

export const notifyIndexNow = async (urls, reason = 'manual_admin_request') => {
  try {
    const response = await api.post('/admin/content/indexnow', { urls, reason });
    return response.data;
  } catch (error) {
    console.error('Error notifying IndexNow:', error);
    throw error;
  }
};

// Dashboard
export const getAdminDashboard = async () => {
  try {
    const response = await api.get('/admin/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard:', error);
    throw error;
  }
};

// Packages (Admin)
export const getAdminPackages = async () => {
  try {
    const response = await api.get('/admin/packages');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin packages:', error);
    throw error;
  }
};

export const createPackage = async (packageData) => {
  try {
    const response = await api.post('/admin/packages', packageData);
    return response.data;
  } catch (error) {
    console.error('Error creating package:', error);
    throw error;
  }
};

export const updatePackage = async (packageId, packageData) => {
  try {
    const response = await api.put(`/admin/packages/${packageId}`, packageData);
    return response.data;
  } catch (error) {
    console.error('Error updating package:', error);
    throw error;
  }
};

export const deletePackage = async (packageId) => {
  try {
    const response = await api.delete(`/admin/packages/${packageId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting package:', error);
    throw error;
  }
};

// Modules (Admin)
export const getAdminModules = async () => {
  try {
    const response = await api.get('/admin/modules');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin modules:', error);
    throw error;
  }
};

export const createModule = async (moduleData) => {
  try {
    const response = await api.post('/admin/modules', moduleData);
    return response.data;
  } catch (error) {
    console.error('Error creating module:', error);
    throw error;
  }
};

export const updateModule = async (moduleId, moduleData) => {
  try {
    const response = await api.put(`/admin/modules/${moduleId}`, moduleData);
    return response.data;
  } catch (error) {
    console.error('Error updating module:', error);
    throw error;
  }
};

export const deleteModule = async (moduleId) => {
  try {
    const response = await api.delete(`/admin/modules/${moduleId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting module:', error);
    throw error;
  }
};

// Categories (Admin)
export const getAdminCategories = async () => {
  try {
    const response = await api.get('/admin/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin categories:', error);
    throw error;
  }
};

export const createCategory = async (categoryData) => {
  try {
    const response = await api.post('/admin/categories', categoryData);
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

export const updateCategory = async (categoryId, categoryData) => {
  try {
    const response = await api.put(`/admin/categories/${categoryId}`, categoryData);
    return response.data;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const response = await api.delete(`/admin/categories/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// Plans (Admin)
export const getAdminPlans = async () => {
  try {
    const response = await api.get('/admin/plans');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin plans:', error);
    throw error;
  }
};

export const createPlan = async (planData) => {
  try {
    const response = await api.post('/admin/plans', planData);
    return response.data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
};

export const updatePlan = async (planId, planData) => {
  try {
    const response = await api.put(`/admin/plans/${planId}`, planData);
    return response.data;
  } catch (error) {
    console.error('Error updating plan:', error);
    throw error;
  }
};

export const deletePlan = async (planId) => {
  try {
    const response = await api.delete(`/admin/plans/${planId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting plan:', error);
    throw error;
  }
};

// Orders (Admin)
export const getAdminOrders = async (status = null) => {
  try {
    const params = status ? { status } : {};
    const response = await api.get('/admin/orders', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin orders:', error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await api.put(`/admin/orders/${orderId}/status?status=${status}`);
    return response.data;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Leads (Admin)
export const getAdminLeads = async (status = null) => {
  try {
    const params = status ? { status } : {};
    const response = await api.get('/admin/leads', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin leads:', error);
    throw error;
  }
};

export const updateLeadStatus = async (leadId, status) => {
  try {
    const response = await api.put(`/admin/leads/${leadId}/status?status=${status}`);
    return response.data;
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw error;
  }
};

// Health check
export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

export default api;
